import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Languages,
  Loader2,
  LockKeyhole,
  Mail,
  UtensilsCrossed,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supportedLanguages } from '../../i18n/config';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Auth/auth.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const languageRef = useRef(null);

  const { t, language, changeLanguage } = useLanguage();
  const { isAuthenticated, login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const redirectTo = location.state?.from?.pathname || '/dashboard';

  const selectedLanguage =
    supportedLanguages.find((item) => item.code === language) ||
    supportedLanguages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target)
      ) {
        setLanguageOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const updateField = (field, value) => {
    setFormData((currentData) => ({
      ...currentData,
      [field]: value,
    }));

    if (localError) {
      setLocalError('');
    }
  };

  const handleSelectLanguage = (languageCode) => {
    changeLanguage(languageCode);
    setLanguageOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.email.trim() || !formData.password) {
      setLocalError(t('auth.errors.required'));
      return;
    }

    try {
      setSubmitting(true);
      setLocalError('');

      await login({
        email: formData.email.trim(),
        password: formData.password,
      });

      navigate(redirectTo, {
        replace: true,
      });
    } catch (error) {
      setLocalError(translateApiError(error, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-background" aria-hidden="true">
        <span className="auth-background__shape auth-background__shape--one" />
        <span className="auth-background__shape auth-background__shape--two" />
        <span className="auth-background__shape auth-background__shape--three" />
      </div>

      <div className="auth-language" ref={languageRef}>
        <button
          type="button"
          className={`auth-language__trigger ${
            languageOpen ? 'auth-language__trigger--open' : ''
          }`}
          onClick={() =>
            setLanguageOpen((currentValue) => !currentValue)
          }
          aria-label={t('header.language')}
          aria-expanded={languageOpen}
        >
          <span className="auth-language__icon">
            <Languages size={17} />
          </span>

          <span className="auth-language__current">
            {selectedLanguage.label}
          </span>

          <ChevronDown
            className="auth-language__chevron"
            size={16}
          />
        </button>

        {languageOpen && (
          <div className="auth-language__menu">
            {supportedLanguages.map((item) => {
              const isSelected = item.code === language;

              return (
                <button
                  type="button"
                  key={item.code}
                  className={`auth-language__option ${
                    isSelected
                      ? 'auth-language__option--active'
                      : ''
                  }`}
                  onClick={() =>
                    handleSelectLanguage(item.code)
                  }
                >
                  <span className="auth-language__option-content">
                    <strong>{item.label}</strong>
                    <small>{item.code.toUpperCase()}</small>
                  </span>

                  <span className="auth-language__check">
                    {isSelected && <Check size={15} />}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <section className="auth-card">
        <div className="auth-card__accent" aria-hidden="true" />

        <div className="auth-card__content">
          <header className="auth-card__header">
            <div className="auth-card__brand">
              <span className="auth-card__logo">
                <UtensilsCrossed size={28} strokeWidth={2.1} />
              </span>

              <div className="auth-card__brand-text">
                <p>{t('auth.eyebrow')}</p>
                <h1>{t('auth.title')}</h1>
              </div>
            </div>

            <p className="auth-card__description">
              {t('auth.description')}
            </p>
          </header>

          <form className="auth-form" onSubmit={handleSubmit}>
            {localError && (
              <div className="auth-error" role="alert">
                {localError}
              </div>
            )}

            <label className="auth-field">
              <span className="auth-field__label">
                {t('auth.email')}
              </span>

              <div className="auth-field__control">
                <span className="auth-field__icon">
                  <Mail size={18} />
                </span>

                <input
                  type="email"
                  value={formData.email}
                  placeholder={t('auth.emailPlaceholder')}
                  onChange={(event) =>
                    updateField('email', event.target.value)
                  }
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={submitting}
                />
              </div>
            </label>

            <label className="auth-field">
              <span className="auth-field__label">
                {t('auth.password')}
              </span>

              <div className="auth-field__control">
                <span className="auth-field__icon">
                  <LockKeyhole size={18} />
                </span>

                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  placeholder={t('auth.passwordPlaceholder')}
                  onChange={(event) =>
                    updateField('password', event.target.value)
                  }
                  autoComplete="current-password"
                  disabled={submitting}
                />

                <button
                  type="button"
                  className="auth-field__password-toggle"
                  onClick={() =>
                    setShowPassword(
                      (currentValue) => !currentValue
                    )
                  }
                  aria-label={t('auth.togglePassword')}
                  disabled={submitting}
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </label>

            <button
              type="submit"
              className="auth-submit"
              disabled={submitting}
            >
              <span className="auth-submit__content">
                {submitting ? (
                  <Loader2
                    className="auth-submit__loader"
                    size={18}
                  />
                ) : (
                  <LockKeyhole size={18} />
                )}

                <span>
                  {submitting
                    ? t('auth.signingIn')
                    : t('auth.signIn')}
                </span>
              </span>
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Login;